import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { subDays, endOfToday, startOfWeek, startOfMonth, format, addDays } from "date-fns";
import filters from "../constants/filters";

const AppContext = createContext();

const AppProvider = ({ children }) => {
  const [incoming, setIncoming] = useState(false);
  const [outgoing, setOutgoing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [preventSearchReset, setPreventSearchReset] = useState(false);
  const [operatorsWithMetrics, setOperatorsWithMetrics] = useState([]);
  const [teamGrades, setTeamGrades] = useState({
    min_team_grade: 0,
    max_team_grade: 10,
  });
  const [individualGrades, setIndividualGrades] = useState({
    min_individual_grade: 0,
    max_individual_grade: 10,
    exclude_na: false,
  });
  let slide_timer;

  const [selectedDepartment, setSelectedDepartment] = useState([filters[0].value]);
  // Dashboard-only filters. Kept apart from the calls-page `selectedDepartment`
  // / `incoming` / `outgoing`, which the navbar resets on every route change.
  const [dashboardDepartment, setDashboardDepartment] = useState("all");
  const [dashboardDirection, setDashboardDirection] = useState("all");
  // Operators-page department filter ("all" or a department id).
  const [representativesDepartment, setRepresentativesDepartment] = useState("all");
  const [dateRange, setDateRange] = useState({
    start_date: subDays(new Date(), new Date().getDay()),
    end_date: new Date(),
  });

  // Data for persistent Audio Player

  const popupAudioRef = useRef(null);
  const sidebarAudioRef = useRef(null);

  const [popupAudioUrl, setPopupAudioUrl] = useState(null);
  const [popupIsAudioPlaying, setPopupIsAudioPlaying] = useState(false);
  const [isInviteMemberModalDisplayed, setIsInviteMemberModalDisplayed] = useState(false);
  const [popupAutoPlay, setPopupAutoPlay] = useState(false);
  const [popupAudioTime, setPopupAudioTime] = useState(0);
  const [popupAudioDuration, setPopupAudioDuration] = useState(0);
  const [popupAudioSpeed, setPopupAudioSpeed] = useState(1);
  // Id of the call loaded in each player, so a call playing in the background
  // can be reopened from the sidebar.
  const [popupCallId, setPopupCallId] = useState(null);

  const [sidebarAudioUrl, setSidebarAudioUrl] = useState(null);
  const [sidebarIsAudioPlaying, setSidebarIsAudioPlaying] = useState(false);
  const [sidebarAutoPlay, setSidebarAutoPlay] = useState(true);
  const [sidebarAudioTime, setSidebarAudioTime] = useState(0);
  const [sidebarAudioDuration, setSidebarAudioDuration] = useState(0);
  const [sidebarAudioSpeed, setSidebarAudioSpeed] = useState(1);
  const [sidebarCallId, setSidebarCallId] = useState(null);

  // Data for persistent calendar dates
  const startOfWeekDate = startOfWeek(new Date(), { weekStartsOn: 0 }); // weekStartsOn: 1 sets the start of the week to Monday
  const startOfMonthDate = startOfMonth(new Date());
  const endOfTodayDate = endOfToday();
  const dayOfWeekString = format(new Date(), "EEEE");
  const isMonday = dayOfWeekString === "Monday";

  const callsStartDate = isMonday ? addDays(startOfWeekDate, -6) : startOfWeekDate;
  const callsEndDate = isMonday ? addDays(endOfTodayDate, -1) : endOfTodayDate;

  const [financialDateRange, setFinancialDateRange] = useState({
    startDate: startOfMonthDate,
    endDate: endOfTodayDate,
  });

  const [callsDateRange, setCallsDateRange] = useState({
    startDate: callsStartDate,
    endDate: callsEndDate,
  });

  useEffect(() => {
    const delaySearch = setTimeout(async () => {
      setDebouncedSearchText(searchText);
    }, 700);

    return () => clearTimeout(delaySearch);
  }, [searchText]);

  const handleOnGradeChange = (type = "individual", grades) => {
    clearTimeout(slide_timer);
    switch (type) {
      case "individual":
        slide_timer = setTimeout(async () => {
          setIndividualGrades(grades);
        }, 1000);
        break;
      case "team":
        slide_timer = setTimeout(async () => {
          setTeamGrades(grades);
        }, 1000);
        break;
      default:
        break;
    }
  };

  return (
    <AppContext.Provider
      value={{
        incoming,
        setIncoming,
        outgoing,
        setOutgoing,
        searchText,
        setSearchText,
        selectedDepartment,
        setSelectedDepartment,
        dashboardDepartment,
        setDashboardDepartment,
        dashboardDirection,
        setDashboardDirection,
        representativesDepartment,
        setRepresentativesDepartment,
        dateRange,
        setDateRange,
        debouncedSearchText,
        preventSearchReset,
        setPreventSearchReset,
        operatorsWithMetrics,
        setOperatorsWithMetrics,
        individualGrades,
        handleOnGradeChange,
        teamGrades,
        setTeamGrades,
        financialDateRange,
        setFinancialDateRange,
        callsDateRange,
        setCallsDateRange,
        popupAudioRef,
        sidebarAudioRef,
        popupAudioUrl,
        popupIsAudioPlaying,
        popupAutoPlay,
        popupAudioTime,
        sidebarAudioUrl,
        sidebarIsAudioPlaying,
        sidebarAutoPlay,
        sidebarAudioTime,
        setPopupAudioUrl,
        setPopupIsAudioPlaying,
        setPopupAutoPlay,
        setPopupAudioTime,
        setSidebarAudioUrl,
        setSidebarIsAudioPlaying,
        setSidebarAutoPlay,
        setSidebarAudioTime,
        popupAudioDuration,
        setPopupAudioDuration,
        sidebarAudioDuration,
        setSidebarAudioDuration,
        popupAudioSpeed,
        setPopupAudioSpeed,
        sidebarAudioSpeed,
        setSidebarAudioSpeed,
        popupCallId,
        setPopupCallId,
        sidebarCallId,
        setSidebarCallId,
        isInviteMemberModalDisplayed,
        setIsInviteMemberModalDisplayed,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);

export default AppProvider;
